import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import '../../css/Pages.css';
import ScienceScoringReferenceForm from '../../components/Scoring/ScienceScoring/ScienceScoringReferenceForm';

const ScienceScoringNew = () => {
    const toast = useRef(null);
    const [calcResult, setCalcResult] = useState(null);
    const [isCalculating, setIsCalculating] = useState(true);

    const currentYear = new Date().getFullYear();
    const fromDateStr = `${currentYear}-01-01`;
    const toDateStr = `${currentYear}-12-31`;

    const [isRefModalOpen, setIsRefModalOpen] = useState(false);
    const [refFormData, setRefFormData] = useState({
        ScoringContent: 'All',
        ArticleRanking: 'All',
        ItemStatusFilter: 'All',
        Coefficient: 1,
        ActionType: 'View',
        FromDate: fromDateStr,
        ToDate: toDateStr
    });

    const renderHighlightedAuthors = (authorsStr) => {
        const userStr = localStorage.getItem('user');
        if (!userStr || !authorsStr) return authorsStr;

        const currentUser = JSON.parse(userStr);
        const fullName = `${currentUser.LastName || ''} ${currentUser.FirstName || ''}`.trim();
        if (!fullName) return authorsStr;

        const removeAccents = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

        const normFullName = removeAccents(fullName).trim();
        const nameParts = normFullName.split(/\s+/);
        const lastName = nameParts[0];
        const firstName = nameParts[nameParts.length - 1];

        const chunks = authorsStr.split(/([,;]|\s+and\s+|\s+và\s+)/i);

        return chunks.map((chunk, index) => {
            if (index % 2 !== 0) return <span key={index}>{chunk}</span>;

            const normChunk = removeAccents(chunk).trim();
            const chunkWords = normChunk.replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);

            let isMatch = false;
            const hasLastName = chunkWords.includes(lastName);
            const hasFirstName = chunkWords.includes(firstName);
            const hasFirstInitial = chunkWords.some(w => w === firstName[0] && w.length === 1);
            const hasLastInitial = chunkWords.some(w => w === lastName[0] && w.length === 1);

            if (normChunk === normFullName || normChunk.includes(normFullName)) {
                isMatch = true;
            } else if (hasLastName && (hasFirstName || hasFirstInitial)) {
                isMatch = true;
            } else if (hasFirstName && (hasLastName || hasLastInitial)) {
                isMatch = true;
            } else if (normChunk.replace(/\s+/g, '') === nameParts.map(p => p[0]).join('')) {
                isMatch = true;
            }

            if (isMatch) {
                return <strong key={index} style={{ color: '#0056b3', fontWeight: 'bold' }}>{chunk}</strong>;
            }
            return <span key={index}>{chunk}</span>;
        });
    };

    const processAutoScoring = useCallback(async () => {
        setIsCalculating(true);
        try {
            const userStr = localStorage.getItem('user');
            const accessToken = localStorage.getItem('accessToken');
            if (!userStr || !accessToken) return;

            const currentUser = JSON.parse(userStr);
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            };

            let existingId = null;
            let existingStatus = null;

            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
                ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

            const existingRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoring?userId=${currentUser.Id}&isAdmin=${isManager}`, { headers });

            if (existingRes.ok) {
                const existingList = await existingRes.json();
                const match = existingList.find(d => {
                    if (!d.FromDate || !d.ToDate) return false;
                    return d.FromDate.startsWith(fromDateStr) &&
                        d.ToDate.startsWith(toDateStr) &&
                        d.ScoringContent === 'All' &&
                        d.Status !== 'Tham khảo';
                });
                if (match) {
                    existingId = match.Id;
                    existingStatus = match.Status;
                }
            }

            const calcPayload = {
                ScoringContent: 'All',
                ArticleRanking: 'All',
                ItemStatusFilter: 'Approved',
                Coefficient: 1,
                FromDate: fromDateStr,
                ToDate: toDateStr,
                UserId: currentUser?.Id
            };

            const calcRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoring?action=calculatescore`, {
                method: 'POST',
                headers,
                body: JSON.stringify(calcPayload)
            });

            if (calcRes.ok) {
                const result = await calcRes.json();
                if (result.status === 'error' || !result.Summary) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: result.message || 'Tính toán thất bại!' });
                    setIsCalculating(false);
                    return;
                }

                setCalcResult(result);

                if (existingStatus !== 'Đã duyệt' && existingStatus !== 'Đã gửi duyệt') {
                    const saveData = {
                        ...calcPayload,
                        ...result.Summary,
                        Id: existingId || 0,
                        FromDate: fromDateStr,
                        ToDate: toDateStr,
                        UserId: currentUser?.Id
                    };

                    const method = existingId ? 'PUT' : 'POST';
                    const saveRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoring`, {
                        method,
                        headers,
                        body: JSON.stringify(saveData)
                    });

                    if (saveRes.ok) {
                        const saveResult = await saveRes.json();
                        if (saveResult.status !== 'error') {
                            toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã tự động tính và cập nhật điểm vào hệ thống!' });
                        }
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi lưu dữ liệu', detail: 'Không thể lưu bản ghi vào hệ thống' });
                    }
                } else {
                    toast.current?.show({ severity: 'info', summary: 'Thông báo', detail: 'Bảng điểm năm nay đã được gửi/duyệt nên hệ thống chỉ hiển thị chứ không cập nhật đè.' });
                }
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
        } finally {
            setIsCalculating(false);
        }
    }, [fromDateStr, toDateStr]);

    useEffect(() => {
        processAutoScoring();
    }, [processAutoScoring]);

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" />

            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div className="header-title">
                    <h2 style={{ margin: 0 }}>TÍNH ĐIỂM KHOA HỌC</h2>
                </div>
                <button
                    type="button"
                    onClick={() => setIsRefModalOpen(true)}
                    style={{
                        backgroundColor: '#17a2b8',
                        color: '#fff',
                        border: 'none',
                        padding: '10px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                >
                    <i className="fa-solid fa-calculator"></i>
                    Tính điểm tham khảo
                </button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '25px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <div className="form-group" style={{ flex: '0 1 250px', marginBottom: 0 }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Từ ngày *</label>
                    <input type="text" className="form-input" value={`01/01/${currentYear}`} readOnly style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed', width: '100%', color: '#495057', border: '1px solid #ced4da' }} />
                </div>
                <div className="form-group" style={{ flex: '0 1 250px', marginBottom: 0 }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Đến ngày *</label>
                    <input type="text" className="form-input" value={`31/12/${currentYear}`} readOnly style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed', width: '100%', color: '#495057', border: '1px solid #ced4da' }} />
                </div>
            </div>

            {isCalculating && (
                <div style={{ textAlign: 'center', padding: '50px 0' }}>
                    <i className="fa-solid fa-circle-notch fa-spin fa-3x" style={{ color: '#007bff', marginBottom: '15px' }}></i>
                    <h4 style={{ color: '#555' }}>Đang tự động thu thập công trình và tính toán số liệu...</h4>
                </div>
            )}

            {!isCalculating && calcResult && calcResult.Summary && (
                <div style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>

                    <h4 style={{ color: '#555', marginBottom: '15px', textTransform: 'uppercase', fontSize: '15px' }}>
                        TỔNG HỢP SỐ GIỜ NGHIÊN CỨU KHOA HỌC
                    </h4>
                    <div style={{ overflowX: 'auto', marginBottom: '35px' }}>
                        <table className="custom-table" style={{ width: '100%', textAlign: 'center' }}>
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th style={{ textAlign: 'center' }}>Chức danh</th>
                                    <th style={{ textAlign: 'center' }}>Định mức chuẩn (giờ)</th>
                                    <th style={{ textAlign: 'center' }}>Tỉ lệ giảm (%)</th>
                                    <th style={{ textAlign: 'center' }}>Cần đạt (giờ)</th>
                                    <th style={{ textAlign: 'center' }}>Tổng điểm (điểm)</th>
                                    <th style={{ textAlign: 'center' }}>Hệ số</th>
                                    <th style={{ textAlign: 'center' }}>Số giờ quy đổi (giờ)</th>
                                    <th style={{ textAlign: 'center' }}>Chênh lệch (giờ)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{calcResult.Summary.JobTitle}</td>
                                    <td>{calcResult.Summary.StandardHours}</td>
                                    <td>{Math.round(Number(calcResult.Summary.ReductionPercentage) * 100) / 100}</td>
                                    <td style={{ fontWeight: 'bold', color: '#e67e22' }}>{Math.round(Number(calcResult.Summary.RequiredHours) * 100) / 100}</td>
                                    <td style={{ fontWeight: 'bold' }}>{Math.round(Number(calcResult.Summary.AchievedScore) * 100) / 100}</td>
                                    <td>{calcResult.Summary.Coefficient}</td>
                                    <td style={{ fontWeight: 'bold' }}>{calcResult.Summary.ConvertedHours}</td>
                                    <td style={{ color: calcResult.Summary.DifferenceHours >= 0 ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                                        {Math.round(Number(calcResult.Summary.DifferenceHours) * 100) / 100}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h4 style={{ color: '#555', marginBottom: '15px', textTransform: 'uppercase', fontSize: '15px' }}>
                        CHI TIẾT CÔNG TRÌNH (CHỈ TÍNH CÁC BẢN GHI ĐÃ DUYỆT)
                    </h4>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="custom-table" style={{ width: '100%', textAlign: 'center' }}>
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th style={{ textAlign: 'center', width: '4%' }}>STT</th>
                                    <th style={{ textAlign: 'left', width: '10%' }}>Loại</th>
                                    <th style={{ textAlign: 'left', width: '22%' }}>Tiêu đề</th>
                                    <th style={{ textAlign: 'left', width: '15%' }}>Nơi đăng/Cấp</th>
                                    <th style={{ textAlign: 'center', width: '7%' }}>Điểm tạp chí</th>
                                    <th style={{ textAlign: 'center', width: '8%' }}>Ngày NT/XB</th>
                                    <th style={{ textAlign: 'left', width: '16%' }}>Tác giả</th>
                                    <th style={{ textAlign: 'left', width: '11%' }}>Vai trò</th>
                                    <th style={{ textAlign: 'center', width: '7%' }}>Số điểm</th>
                                </tr>
                            </thead>
                            <tbody>
                                {calcResult.Details && calcResult.Details.length > 0 ? (
                                    calcResult.Details.map((d, idx) => (
                                        <tr key={idx}>
                                            <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                            <td style={{ fontWeight: 'bold', color: '#666', fontSize: '12px', textAlign: 'left' }}>{d.Type}</td>
                                            <td style={{ textAlign: 'left', fontSize: '13px' }}>{d.Title}</td>
                                            <td style={{ textAlign: 'left', fontSize: '13px' }}>{d.Journal}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#e67e22' }}>{d.BaseScore ?? '-'}</td>
                                            <td style={{ textAlign: 'center', fontSize: '12px' }}>{d.PublishDate}</td>
                                            <td style={{ fontSize: '13px', textAlign: 'left', lineHeight: '1.4' }}>
                                                {renderHighlightedAuthors(d.Authors)}
                                            </td>
                                            <td style={{ textAlign: 'left', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                                <span style={{ backgroundColor: '#f1f5f9', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', display: 'inline-block', whiteSpace: 'nowrap' }}>
                                                    {d.Role || '-'}
                                                </span>
                                            </td>
                                            <td style={{ fontWeight: 'bold', color: '#0056b3', textAlign: 'center', fontSize: '14px' }}>
                                                {Number(d.Score).toFixed(2)}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
                                            Không có dữ liệu trong khoảng thời gian này
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <ScienceScoringReferenceForm
                isOpen={isRefModalOpen}
                onClose={() => setIsRefModalOpen(false)}
                formData={refFormData}
                setFormData={setRefFormData}
                toast={toast}
            />
        </div>
    );
};

export default ScienceScoringNew;